import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CreditCard, Zap } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";

const Billing_v2 = () => {
  const navigate = useNavigate();
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscriptionInfo();
  }, []);

  const loadSubscriptionInfo = async () => {
    try {
      const response = await axios.get(`${API}/subscription/info`, {
        withCredentials: true
      });
      setSubscriptionInfo(response.data);
    } catch (error) {
      toast.error('Failed to load subscription info');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  const tierPrices = {
    essential: 9.99,
    standard: 19.99,
    plus: 39.99
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-6">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-heading font-bold text-white">DueDay</span>
          </div>
        </div>
      </nav>

      <div className="container mx-auto max-w-4xl pt-16">
        <Button 
          data-testid="back-to-dashboard-btn"
          variant="ghost" 
          onClick={() => navigate('/dashboard')}
          className="mb-6 rounded-full text-white hover:text-yellow-400 hover:bg-white/10"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
        </Button>

        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <CreditCard className="h-10 w-10 text-yellow-400" />
            <h1 className="text-4xl font-heading font-bold text-white" data-testid="billing-title">Billing</h1>
          </div>

          {/* Current Plan */}
          <div className="mb-12">
            <h2 className="text-2xl font-heading font-semibold mb-4 text-white">Current Plan</h2>
            {subscriptionInfo?.tier && subscriptionInfo.tier !== 'none' ? (
              <div className="p-6 bg-yellow-400/10 rounded-2xl border border-yellow-400/30" data-testid="current-plan">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-heading font-bold capitalize text-white">
                      {subscriptionInfo.tier} Plan
                    </h3>
                    <p className="text-slate-400 mt-1">
                      Status: <span className="capitalize font-semibold text-green-400">{subscriptionInfo.status}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-heading font-bold text-yellow-400">
                      ${tierPrices[subscriptionInfo.tier]}
                    </div>
                    <p className="text-sm text-slate-400">per month</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-slate-700">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-slate-400">Per Event Limit</p>
                      <p className="font-semibold text-white">${subscriptionInfo.limits?.per_event || 0}</p>
                    </div>
                    <div>
                      <p className="text-slate-400">30-Day Limit</p>
                      <p className="font-semibold text-white">${subscriptionInfo.limits?.per_30_days || 0}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 bg-slate-900/50 rounded-2xl border border-slate-700 text-center" data-testid="no-plan">
                <p className="text-slate-400 mb-4">You don't have an active subscription</p>
                <Button 
                  data-testid="subscribe-btn"
                  onClick={() => navigate('/select-tier')}
                  className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full px-8 py-6 font-bold"
                >
                  Subscribe Now
                </Button>
              </div>
            )}
          </div>

          {/* Plan Options */}
          {subscriptionInfo?.tier && subscriptionInfo.tier !== 'none' && (
            <div className="mb-12">
              <h2 className="text-2xl font-heading font-semibold mb-4 text-white">Change Plan</h2>
              <p className="text-slate-400 mb-6">
                Want to upgrade or downgrade? Contact us to modify your subscription.
              </p>
              <div className="flex gap-4">
                <Button 
                  data-testid="upgrade-btn"
                  onClick={() => navigate('/select-tier')}
                  className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8"
                  variant="outline"
                >
                  View Plans
                </Button>
              </div>
            </div>
          )}

          {/* Payment Information */}
          <div>
            <h2 className="text-2xl font-heading font-semibold mb-4 text-white">Payment Information</h2>
            <div className="p-6 bg-slate-900/50 rounded-2xl border border-slate-700">
              <p className="text-sm text-slate-400 mb-4">
                Your payments are securely processed by Stripe. All subscription charges are handled automatically.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Payment Method</span>
                  <span className="font-medium text-white">Card ending in ****</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Next Billing Date</span>
                  <span className="font-medium text-white">
                    {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Cancel Section */}
          {subscriptionInfo?.tier && subscriptionInfo.tier !== 'none' && (
            <div className="mt-12 pt-8 border-t border-slate-700">
              <h2 className="text-2xl font-heading font-semibold mb-4 text-white">Cancel Subscription</h2>
              <p className="text-slate-400 mb-6">
                Need to cancel? We're sorry to see you go. Your coverage will remain active until the end of your billing period.
              </p>
              <Button 
                data-testid="cancel-subscription-btn"
                variant="destructive"
                className="rounded-full bg-red-600 hover:bg-red-700"
                onClick={() => toast.info('Please contact support to cancel your subscription')}
              >
                Cancel Subscription
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing_v2;
